/**
 * Novinky — release-notes panel on the dashboard.
 *
 * The data is static — every release is dated + versioned in chronological
 * order (newest first); keep the top entry's version in step with
 * `frontend/package.json`. When you cut a new release, prepend an entry here.
 */

import type { ReactNode } from 'react';

export interface NovinkySection {
  heading: string;
  items: ReactNode[];
}

export interface NovinkyEntry {
  version: string;
  date: string;
  sections: NovinkySection[];
}

/** Current app version — always the newest NOVINKY entry (single source of
 * truth for the rail badge; keep frontend/package.json in step). */
export const APP_VERSION = 'v8.12.0';

export const NOVINKY: NovinkyEntry[] = [
  {
    version: 'v8.12.0',
    date: '3. 11. 2026',
    sections: [
      {
        heading: '🚙 Půjčovna: rezervace',
        items: [
          'Nový typ rezervace Servis: vůz je po dobu opravy zablokovaný pro pronájem. V kalendáři i v seznamech je červený se štítkem Servis, nemá zákazníka ani cenu a nepředává se',
          'Rezervace už nemá nejdelší dobu trvání (dřív 366 dní), takže dlouhodobé blokace a zápůjčky jdou zadat i upravit. Veřejný web zůstává u 365 dní',
          'Přehled: kliknutím na řádek se otevře rezervace, kterou můžete změnit, zkrátit nebo prodloužit',
        ],
      },
      {
        heading: '💳 Půjčovna: platby',
        items: [
          'V seznamu rezervací je sloupec Zaplaceno a u každé rezervace tlačítko Platby: stav úhrad, Označit jako zaplaceno (platba na pobočce) a pro správce Vrátit. Platby, které dorazily pozdě nebo navíc, najde správce ve Správě v nástroji Platby k řešení',
          'Nová záložka Úhrady: všechny úhrady napříč rezervacemi po týdnech, s filtrem podle původu (brána, pobočka, původní systém)',
          'Správa → Nastavení: E-maily nájemcům. Nájemce dostane potvrzení platby a zrušení nezaplacené rezervace jménem vaší firmy',
        ],
      },
      {
        heading: '🚗 Vozový park půjčovny',
        items: [
          'U vozu lze samostatně označit Náhradní vozidlo v servisu a Zveřejnit na webu (správce půjčovny). V servisní události se jako náhradní vozidlo nabízejí jen vozy s tímto označením',
          'Ze starého systému pujcsiauto.online přibyly vozy, rezervace a úhrady pro Trutnov a Ostravu / Opavu',
        ],
      },
      {
        heading: '🔧 Další změny',
        items: [
          'Termíny STK a servisní prohlídky na přehledu a v kalendáři ukazují vozy všech firem skupiny, ke kterým máte přístup',
        ],
      },
    ],
  },
  {
    version: 'v8.11.1',
    date: '30. 10. 2026',
    sections: [
      {
        heading: '🛠️ Servis: ukládání událostí',
        items: [
          'Když formulář události nejde uložit, hned se zobrazí důvod. Dřív mohlo tlačítko Vytvořit vypadat, že nic nedělá, třeba u termínu, který už začal, nebo u vozidla s neplatným VIN',
          'VIN vozidla vybraného z evidence se přebírá bez mezer a velkými písmeny, stejně jako při ručním zadání',
        ],
      },
    ],
  },
  {
    version: 'v8.11.0',
    date: '26. 10. 2026',
    sections: [
      {
        heading: '🛠️ Servis: e-maily zákazníkovi',
        items: [
          'V okně servisní události je volba Poslat k této objednávce e-maily, která platí jen pro danou objednávku (ne pro zákazníka). Odškrtnutím zákazník nedostane potvrzení objednávky, e-mail o změně termínu ani připomenutí 48 hodin předem. Volba se ukládá u objednávky, při dalším otevření ji uvidíte tak, jak jste ji uložili, a opětovným zaškrtnutím e-maily zase zapnete',
          'Volba se nabízí jen firmám, které mají v Správě firmy nastavené Servis e-maily (jinak se nic neodesílá), a jen u události s platným e-mailem',
        ],
      },
      {
        heading: '🚙 Půjčovna',
        items: [
          'Kalendář rezervací: v buňce vozu je navíc barva a motor, takže od sebe snáz rozeznáte podobné vozy',
          'Ze starého systému pujcsiauto.online přibyli klienti jako zákazníci s řidiči, doklady a skeny dokladů',
        ],
      },
      {
        heading: '🚗 Další změny',
        items: [
          'Seznam vozidel jde prohledávat i podle umístění vozu',
          'Uskladnění: Nová výměna nabízí zákazníky a vozidla při psaní, podle SPZ najde sady vozu a předvyplní sundanou a nasazenou sadu',
        ],
      },
    ],
  },
  {
    version: 'v8.10.0',
    date: '23. 10. 2026',
    sections: [
      {
        heading: '🚙 Půjčovna: nový modul',
        items: [
          'Půjčovna má vlastní modul s přehledem, rezervacemi, vozovým parkem a správou. Vozidla do ní zařadíte z karty nového nebo ojetého vozu (akce Zařadit do půjčovny); u každého vozu vidíte, v jaké rezervaci je právě teď a jaká následuje',
          'Přehled: dnešní nástěnka půjčovny — vozy po termínu, čekající na předání, dnes a zítra k vrácení a k předání; z řádku vůz rovnou předáte nebo přijmete',
          'Vozový park: ceny za den podle tarifu, nájezd, kontroly, závady vozu (s mapou poškození a historií oprav), fotky, pojištění a časová osa celé historie vozu; seznam jde prohledávat podle SPZ, VIN, značky, modelu i pobočky',
          'Rezervace: kalendář vůz × den i seznam s číslem rezervace a hledáním; formulář ukazuje dostupnost vozu a kolizi hlásí jen tam, kde se termíny skutečně překrývají. Zákazníka, který u nás ještě není, založíte odkazem ve formuláři. Zrušení se zdůvodněním, náhradní vůz ze Servisu je technická rezervace',
          'Barvy v kalendáři: půjčovna zeleně, servis modře, zaměstnanci oranžově, technická rezervace šedě, vůz po termínu červeně; skončené rezervace zůstávají ve své barvě, jen vyblednou',
          'Předání a přijetí vozu: řidiči s doklady, nájezd, kontrolní seznam, kauce, fotky a poznámka; při předání vás upozorníme na propadlou nebo končící STK a servisní prohlídku, otevřené kontroly a evidované závady (jen upozornění, předání nebrání). Při přijetí se zapisují nové závady a vůz se přeceňuje na skutečně využité dny',
          'Cena pronájmu: ceník v tarifech (marže, délky, sezóny), cenové skupiny a věrnostní sleva, sleva a příplatek za zahraničí, příplatek za překročené km a kauce nastavitelná pro každou rezervaci; pojišťovny mají vlastní sazby',
          'Tisk nájemní smlouvy a předávacího protokolu (česky i anglicky) z uložených údajů předání',
          'Ze starého systému pujcsiauto.online jsme převzali vybraná vozidla s historií pronájmů, protokoly a zákazníky',
        ],
      },
      {
        heading: '🚗 Vozidla a převody',
        items: [
          'Seznam ojetých vozidel má sloupec Zákazník a hledání podle zákazníka; počty v záhlaví platí pro celý výsledek',
          'Ojeté vozidlo jiné firmy skupiny jde ze společného seznamu upravit po sekcích přímo v jeho firmě; při převodu do jiné firmy se přenáší i interní poznámka a text rezervace vozu',
        ],
      },
      {
        heading: '🛠️ Další změny',
        items: [
          'Pošta: společnosti pro zásilky se spravují v Pošta → Správa → Společnosti',
          'Historie změn se řadí podle toho, kdy se změna stala, ne podle pořadí zápisu; celé historie záznamů se ukazují i na kartě sady pneumatik a v okně servisní události',
          'Uskladnění: sezóna a stav sady pneumatik jsou štítky',
          'Tisk nabídek a smluv ojetých i nových vozidel nově nerozděluje řádky, cenový blok ani podpisy na dvě stránky',
        ],
      },
    ],
  },
  {
    version: 'v8.9.0',
    date: '9. 10. 2026',
    sections: [
      {
        heading: '🌐 Nová adresa intranetu',
        items: [
          'Intranet je nově na adrese intranet.management; původní adresa intranet.autotrutnov.cz zatím funguje dál. Přihlášení se mezi adresami nepřenáší, na nové adrese se tedy přihlaste znovu',
        ],
      },
      {
        heading: '🏢 Skupina firem: společné seznamy a převody vozů',
        items: [
          'Máte-li přístup do více firem jedné skupiny, najdete v seznamu nových i ojetých vozidel filtr „Aktuální firma / Všechny firmy skupiny“ — vozy všech vašich firem jsou pohromadě a u vozu jiné firmy je štítek s jejím názvem. Detail takového vozu se otevře jen pro čtení; upravit ho můžete po přepnutí na jeho firmu',
          'Detail nového i ojetého vozu má novou akci Převést do firmy… — vůz se přesune do evidence jiné firmy skupiny (nové vozidlo může zůstat novým). Zákazník-firma se v cílové firmě najde podle IČO nebo se tam založí; původní záznam zůstane jako Převedeno jen pro historii',
          'V okně převodu je předvyplněná nákupní cena bez DPH (z prodejní ceny ve smlouvě vozu, jinak z jeho prodejní ceny) a zaškrtnutý plátce DPH — obojí můžete změnit',
          'Vůz, který už byl prodán firmě ze skupiny (podle IČO zákazníka), jde zpětně propojit akcí Doplnit převod do firmy…',
          'Zadáte-li ve formuláři vozu VIN, který už eviduje jiná firma skupiny, upozorníme vás',
          'Ve Servisu a v Uskladnění se při výběru vozu nabízejí i vozy ostatních firem skupiny (bez cen a zákazníků); po potvrzení úkonu se nový termín STK / servisu zapíše vozu u jeho vlastníka',
        ],
      },
      {
        heading: '🚗 Vozidla: výbava a smlouvy',
        items: [
          'Nové vozidlo i nabídka z ceníku (Nissan, Suzuki…) nabídnou volitelnou příplatkovou výbavu s cenami; vybraná výbava se uloží a dál ji můžete upravovat',
          'Smlouva k novému vozidlu přebírá i Standardní výbavu z LMS (včetně tisku); smlouva odkazuje na nabídku, ze které vznikla, a nabídka vypisuje své smlouvy',
          'U vozu Ford bez uložené výbavy je na kartě tlačítko Načíst výbavu z LMS',
          'Datum předání v nabídce a smlouvě jde opět smazat; „Platnost nabídky“ na tisku se řídí nastavením firmy (výchozí 7 dní)',
        ],
      },
      {
        heading: '🧾 Premier na kartě vozidla',
        items: [
          'Sekce Zakázky v Premieru se načte až po kliknutí na Načíst z Premieru, takže se karta vozu otevře rychleji; u firmy bez Premieru se nezobrazuje',
          'U zakázek přibyly objednávky: sloupec Objednáno · N / Neobjednáno a karty Objednávky u dodavatelů (se stavem Přijato / Čeká na dodání / Fakturováno) a Objednávky od zákazníků; vypisuje je i detail zakázky',
        ],
      },
      {
        heading: '📞 Kontakty: čas plánovaného kontaktu',
        items: [
          'Plánovaný kontakt může mít volitelný čas — vidíte ho v Plánovaných, v detailu a seznamu kontaktů, na nástěnce, v kalendáři i v denním přehledu na Slacku; v Plánovaných je kontakt s časem po termínu, jakmile čas uplyne (nástěnka a přehled na Slacku řadí podle dne)',
        ],
      },
    ],
  },
  {
    version: 'v8.8.0',
    date: '30. 9. 2026',
    sections: [
      {
        heading: '🔧 Servis: potvrzení posune termíny vozu',
        items: [
          'Když potvrdíte úkon jako Proběhlo, okno ukáže, kterého vozu se týká (podle VIN, u úkonu bez VIN podle SPZ), a nabídne, za kolik měsíců je další servis (předvyplněno 12) nebo STK (24) — každé auto má jiný interval, číslo změníte; termín vozu se nastaví na den úkonu + zadané měsíce a náhled vidíte hned',
          'Servisní prohlídka posune servis, STK posune STK, „Servisní prohlídka + STK“ obojí; Jiné nic. Zapíše se jen budoucí termín, který je pozdější než ten, který vůz už má',
          'Potvrzovat může i kolega jen s čtením — termín vozu se posune také',
          'SPZ se párují bez ohledu na mezery a velikost písmen; když stejná SPZ patří ojetému i novému vozu, aktualizuje se jen ojeté; když SPZ odpovídá více vozům, nic se neaktualizuje. Změna je vidět v historii úkonu i vozu',
        ],
      },
      {
        heading: '🚗 Nová vozidla: STK a servisní prohlídka',
        items: [
          'Nové vozidlo má nově První registraci, Platnost STK a Servisní prohlídku do (detail vozu, sekce Stav & dispozice); první STK formulář navrhne 4 roky po registraci, dokud je ještě v budoucnu',
          'Registrovaná nová vozidla jsou v kartě Termíny na nástěnce i v kalendáři spolu s ojetými (štítek nové / ojeté); zobrazí se podle přístupu k vozidlům a k ojetým vozidlům',
          'Data se přenesou i při kopii vozu do ojetých',
        ],
      },
      {
        heading: '🔍 Hledání podle SPZ',
        items: [
          'Hledání SPZ nově nerozlišuje mezery ani velká a malá písmena v seznamu nových i ojetých vozidel, ve výměnách pneumatik i ve výběru vozidla („5a13251“ najde „5A1 3251“)',
        ],
      },
    ],
  },
  {
    version: 'v8.7.0',
    date: '30. 9. 2026',
    sections: [
      {
        heading: '🔧 Servis: vozidlo podle SPZ nebo VIN',
        items: [
          'V úkonu se vozidlo hledá jedním polem podle SPZ nebo VIN — nejdřív vozidla vybraného klienta, pak celá evidence; u každého vozu je vidět, zda je nové nebo ojeté, a jeho stav (Skladový, Registrovaný, Zákaznický, Předaný…)',
          'Úkon si pamatuje VIN vozu (SPZ není jednoznačná); u nového úkonu je povinná SPZ nebo VIN, stačí jedno. Vůz mimo evidenci se zadá ručně',
          'Vůz, který je v nových i ojetých, se nabízí jen jednou — jako ojetý',
          'Termíny STK a servisních prohlídek ojetých vozů se s naplánovanými úkony párují podle VIN; Vyhledání hledá i podle VIN',
          'Detail nového i ojetého vozu má novou sekci Servisní historie — zákaznické i interní úkony vozu od nejnovějšího (termín, úkon, klient, technik, stav) s odkazem do Vyhledání; párují se podle VIN, jen úkony bez VIN podle SPZ. Vidí ji členové s přístupem do Servisu',
        ],
      },
      {
        heading: '✅ Servis: potvrzení provedení',
        items: [
          'Zákaznický i interní úkon, který už začal, jde potvrdit jako Proběhlo nebo Neproběhlo; neproběhlý úkon lze rovnou naplánovat znovu. Potvrzovat může každý s přístupem do Servisu',
          'V Plánování je seznam „K potvrzení“ (úkony z posledních 72 hodin), stav je vidět na kartách, v detailu i ve Vyhledání (filtr a sloupec); každá změna je v Historii',
        ],
      },
      {
        heading: '🗓️ Servis: plánování úkonu',
        items: [
          'Po zadání začátku se konec sám nastaví na nejbližší možný termín (o půl hodiny později), dokud ho sami nevyberete; časová osa obsazenosti technika se ukáže hned po výběru technika a začátku',
          'Okno úkonu je širší; zákaznický úkon může mít i druh (Servisní prohlídka, STK…); Upravit ve Vyhledání je v obvyklé liště akcí',
          'Potvrzení a připomenutí zákazníkovi se posílají pod jménem, telefonem a e-mailem vlastní firmy (Správa firmy → Servis e-maily); firma bez vyplněných údajů e-maily neposílá',
        ],
      },
      {
        heading: '🕘 Historie změn',
        items: [
          'Jednotná historie „kdo co změnil“ pro Servis, Uskladnění a členy firmy (Správa → Historie) a nově i pro Vozidla, Nabídky, Smlouvy a Kontakty — s původní a novou hodnotou každého pole',
          'Kontakty mají novou záložku Správa s historií leadů (včetně převzetí z LMS a předání prodejce)',
        ],
      },
      {
        heading: '📦 Uskladnění',
        items: [
          'Umístění sady se vybírá z katalogu lokací (s vyhledáváním) místo volného textu — nevznikají téměř stejné lokace; nové lokace se zakládají na stránce Lokace',
        ],
      },
      {
        heading: '📅 Kalendář a nástěnka',
        items: [
          'Předání vozu se v kalendáři ukazuje v den předávacího protokolu („Předáno · …“), jinak v plánovaný den; dříve chyběla předání, u kterých už existoval prázdný protokol. Nový protokol má datum předvyplněné dneškem a je povinné',
          'Karta termínů ojetých vozů na nástěnce ukazuje, kdy je STK nebo prohlídka naplánovaná; termíny se týkají jen vozů Skladový a Registrovaný (Zákaznické už ne)',
          'Úkoly zadané vám a čekající na přijetí mají na nástěnce vlastní kartu s tlačítkem Přijmout',
        ],
      },
      {
        heading: '🎨 Vzhled',
        items: [
          'Štítky (stavy, LMS, Plán, druh a stav vozu…) jsou nově zaoblené s jemným barevným pozadím; v seznamech s úzkým sloupcem se používá menší varianta a stav vozu „Registrovaný“ se už nezkracuje',
        ],
      },
      {
        heading: '🔗 Ford LMS',
        items: [
          'Číslo objednávky v porovnání skladu spáruje jen vůz, který ještě nemá VIN — opakovaně použité číslo už nespáruje starší vůz s novým a „← LMS“ nepřepíše jeho VIN',
        ],
      },
    ],
  },
  {
    version: 'v8.6.0',
    date: '24. 9. 2026',
    sections: [
      {
        heading: '🚗 Ford LMS: převzetí vozů ze skladu',
        items: [
          'V Porovnání skladu (Vozidla → Správa → LMS) má každý vůz v sekci „Pouze v LMS“ tlačítko Převzít — vůz se založí v databázi i se standardní a příplatkovou výbavou z katalogu LMS, správným modelem, cenou bez DPH a stavem (Zákaznický / Skladový)',
          'Převzít lze i vůz ve výrobě bez VIN (podle interního čísla); vůz, který už v databázi je, se nezaloží podruhé',
          'Nový dealer si tak může celou databázi vozů načíst přímo z LMS',
        ],
      },
      {
        heading: '🔍 Porovnání skladu LMS',
        items: [
          'Vozy s umístěním „TU“ / „Přeprava TU“ se nově porovnávají ve všech polích kromě umístění — dříve se u nich rozdíly (cena, stav, provedení…) vůbec nezobrazovaly',
          'Stav „DEALER DELIVERY“ se bere jako doručený vůz stejně jako „ASSUMED DELIVERY“ — umístění a výbava se u něj už neporovnávají, takže vlastní umístění (např. „Opava“) nehlásí rozdíl',
        ],
      },
      {
        heading: '🔧 Servis: interní úkony',
        items: [
          'Interní úkon má druh — Servisní prohlídka, STK, Servisní prohlídka + STK nebo Jiné — a volný popis úkonu u každého druhu; v týdnu, vyhledávání i detailu se zobrazuje „STK · popis“',
          'Interní úkon má vlastní SPZ a vyhledávání podle SPZ ignoruje mezery („5A1 3251“ = „5A13251“)',
        ],
      },
      {
        heading: '📅 Kalendář',
        items: [
          'Termín STK nebo servisní prohlídky ojetého vozu ukazuje „Naplánováno na …“, když servis má na stejnou SPZ naplánovaný interní úkon odpovídajícího druhu (od 30 dní před termínem, bez horní hranice)',
          'Každý druh položky má svou barvu: předání zeleně, plánované kontakty modře, úkoly oranžově; nenaplánovaný termín je červeně, jen když je prošlý nebo do 45 dní',
          'Kalendářní události byly zrušeny — kalendář zobrazuje předání vozů, úkoly, plánované kontakty a termíny vozů',
        ],
      },
    ],
  },
  {
    version: 'v8.5.0',
    date: '24. 9. 2026',
    sections: [
      {
        heading: '📅 Kalendář: plánované kontakty a termíny vozů',
        items: [
          'V kalendáři se nově zobrazují plánované kontakty (v den plánu, s popisem a prodejcem) a termíny STK a servisních prohlídek ojetých vozů skladem — prošlé termíny jsou červeně; kliknutím se otevře detail kontaktu nebo vozu',
          'Nabídka „Zobrazit“ umožňuje zapnout či vypnout úkoly, plánované kontakty i termíny vozů; volba se pamatuje. Přepínač „Moje“ nově omezuje na vás i plánované kontakty',
        ],
      },
      {
        heading: '🔗 Ford LMS: více poboček',
        items: [
          'Firma může mít více LMS účtů (poboček) — nové leady se načítají ze všech poboček a na nástěnce jsou seskupené podle pobočky; převzatý lead si pobočku pamatuje a uzavírá se pod ní',
          'Každý člen vidí a přebírá jen leady svých přiřazených poboček a jen tehdy, má-li vyplněné LMS ID prodejce',
          'Nástroje skladu LMS slučují vozy ze všech poboček — vůz vedený u dvou poboček (i jednou s VIN a jednou bez) se zobrazí jen jednou',
        ],
      },
      {
        heading: '🛠️ Správa firmy a Platforma',
        items: [
          'Správa firmy (Přehled, Členové, EBM, Premier) je oddělená od nové sekce Platforma (Firmy, Uživatelé, Záznamy úloh), která je jen pro správce platformy; staré odkazy se přesměrují',
          'Přiřazení LMS poboček členům se nastavuje v Platforma → Uživatelé u LMS ID; v Členové je vidět jen pro čtení',
        ],
      },
      {
        heading: '🚗 Vozidla a ojeté vozy',
        items: [
          'SPZ v seznamech smluv a nabídek ojetých vozů i v detailu smlouvy vede přímo na kartu vozu',
          'V seznamech smluv a nabídek nových vozů je číslo objednávky pod názvem vozu ve sloupci Vozidlo a odkazuje na kartu vozu',
        ],
      },
    ],
  },
  {
    version: 'v8.4.0',
    date: '22. 9. 2026',
    sections: [
      {
        heading: '🔒 Úkoly: soukromé úkoly a čas připomínky',
        items: [
          'Úkol zadaný sám sobě lze označit jako soukromý — vidíte ho jen vy: nezobrazí se kolegům v seznamu úkolů, na nástěnce ani v kalendáři (ani správcům). Při zadání úkolu sobě je volba předvyplněná, při výběru jiného příjemce se vypne',
          'Soukromé úkoly jsou v seznamu, na nástěnce i v detailu označené zámkem; předáním úkolu kolegovi přestane být soukromý',
          'Připomínka úkolu může mít i konkrétní čas (24h formát HH:MM) — bez času platí pro celý den; čas se zobrazuje i ve zprávách ve Slacku a v denním přehledu',
          'Odkazy na úkoly a kontakty ve zprávách ze Slacku opět vedou na správnou stránku',
        ],
      },
      {
        heading: '👤 Zákazníci: řidiči, doklady a bankovní spojení',
        items: [
          'Na detailu zákazníka přibyla sekce Řidiči — ke každému řidiči lze evidovat občanský průkaz, řidičský průkaz či pas s platností (štítky platný / končí / prošlý) a nahrát sken obou stran dokladu',
          'Zákazník má nově bankovní spojení (číslo účtu i IBAN) s kontrolou správnosti čísla — připraveno pro vracení kaucí v půjčovně',
          'Vyhledávání zákazníků podle stáří poslední smlouvy: výchozí režim najde zákazníky, kteří naposledy koupili před více než N měsíci; druhý režim ukáže ty se smlouvou za posledních N měsíců',
        ],
      },
      {
        heading: '🚗 Vozidla, ojeté vozy a leasing',
        items: [
          'Ojeté vozy: sledování platnosti STK — na nástěnce je karta blížících se a prošlých termínů (STK i servis), platnost STK je vidět i na detailu vozu',
          'Ojeté vozy: nákupní cena se zadává bez DPH i s DPH (druhá hodnota se dopočítá) a marže nově porovnává obě ceny vč. DPH — u nákupu od plátce už není nadhodnocená',
          'Karta vozidla ukazuje odkazy na připravené i uzavřené smlouvy; tlačítko pro přiřazení zákazníka se zobrazí jen tehdy, když vůz zákazníka ještě nemá',
          'Fleet kalkulace počítá procentní předpoklad z ceníkové ceny a přesněji rozlišuje podobně pojmenované modely (např. Transit vs. Transit Custom)',
          'Import ceníku upozorní na modely bez uvedených interiérů a umožní je doplnit ručně; průvodce ceníkem je dostupný pro další značku',
          'Leasing: detail smlouvy zobrazuje i kauci na konci vedle počáteční kauce',
        ],
      },
      {
        heading: '📊 Management: EBM report',
        items: [
          'Rozdělení buňky do více řádků reportu podílem (např. 80 % zůstává, 20 % jinam) i převodem pevné částky podle hodnoty jiné buňky — pravidla platí pro všechny měsíce daného typu výkazu',
          'Zařazení účtu může mít příznak „absolutní hodnota“ — účet se pak vykáže ve Výnosech jako kladná částka bez ohledu na znaménko v exportu',
        ],
      },
      {
        heading: '🖨️ Tisk a drobné opravy',
        items: [
          'Tiskové sestavy mají jednotnou lištu s tlačítkem Zpět vlevo a sjednocený zápis data a místa u podpisů; předávací protokol uvádí jako místo podpisu město zákazníka',
          'Tlačítko „Protokol za pronajímatele“ se nabízí jen u smluv financovaných leasingem',
          'Konfigurace vozu Ford se tiskne s homologačním razítkem Ford',
          'Vyhodnocení: smlouva se v seznamu zobrazí jen jednou, i když má více předávacích protokolů — opraveny i počty ve statistikách',
        ],
      },
    ],
  },
  {
    version: 'v8.3.0',
    date: '28. 7. 2026',
    sections: [
      {
        heading: '🧾 Zakázky z Premieru na kartě vozidla',
        items: [
          'Karta vozidla nově zobrazuje servisní zakázky z účetního systému Premier vyhledané podle VIN — detail zakázky ukazuje přidružené doklady po skupinách včetně součtů: faktury se stavem úhrady, účetní deník i ostatní deníky',
          'Zakázku lze k vozidlu připojit, nebo ji tlačítkem „Přidat zakázku“ rovnou v Premieru založit — VIN se do zakázky doplní automaticky a vozidlo může mít zakázek více',
          'Čísla propojených zakázek jsou vidět přímo v sekci Stav & dispozice',
        ],
      },
      {
        heading: '🔧 Servis: mazání s historií změn',
        items: [
          'Smazání servisní události už není nevratné — smazané události zůstávají dohledatelné ve Vyhledání přes přepínač „I smazané“, se štítkem Smazáno a detailem ke čtení (kdo a kdy událost smazal)',
          'Každá událost má novou sekci Historie změn: kdo ji vytvořil a kdo co upravil, včetně původní a nové hodnoty každého pole',
          'Zaznamenávají se i automatické úpravy při kolizi termínů — zkrácení či rozdělení sousední události je nyní dohledatelné',
        ],
      },
      {
        heading: '📊 Management a nástěnka',
        items: [
          'Záložka EBM výkazy se přejmenovala na EBM zařazení: mapování účtů podporuje více typů výkazů (výsledovka i rozvaha) a základ pro porovnání nahrajete přímo v záložce',
          'Na nástěnce přibyla karta úkolů po termínu za celou firmu — prošlé plány kontaktů a úkoly firmy jsou přehledně vedle sebe na jednom řádku',
        ],
      },
      {
        heading: '🛠️ Drobná vylepšení a opravy',
        items: [
          'Hledání podle SPZ ignoruje mezery — „1AB 1234“ i „1AB1234“ najdou totéž',
          'Leasing: po úpravě smlouvy přes sekční dialogy se uložená splátka automaticky přepočítá',
          'Panel fleet policy / retail na kartě vozidla lze sbalit, aby nepřekážel',
          'Panel uskladnění pneumatik v servisu se správně řídí oprávněním modulu Uskladnění',
        ],
      },
    ],
  },
  {
    version: 'v8.2.0',
    date: '13. 7. 2026',
    sections: [
      {
        heading: '✏️ Jednotné upravování — všude přes dialogy',
        items: [
          'Úpravy záznamů nyní fungují v celé aplikaci stejně: na detailu má každá sekce tlačítko „Změnit“, které otevře malý dialog jen s poli dané sekce — průvodci slouží už jen k zakládání nových záznamů',
          'Platí pro nabídky a smlouvy nových i ojetých vozů, výkupy, leasing a nově i kartu vozidla (tlačítko „Upravit“ s velkým formulářem nahradilo sedm sekčních dialogů včetně výbavy a interní poznámky)',
          'U ojetých vozů už úprava jedné sekce neprochází zbytkem průvodce — uloží se a zůstanete na detailu',
          'Na telefonu se dialogy otevírají jako spodní list s posuvným obsahem a gestem zavření',
        ],
      },
      {
        heading: '📊 Leasing: kalkulace jako samostatná stránka',
        items: [
          'Výsledná kalkulace (splátka, úrok, náklady) má vlastní stránku dostupnou z detailu smlouvy i nabídky — úrok lze přepočítat a uložit bez procházení průvodce',
          'Detail leasingové smlouvy i nabídky nabízí sekční úpravy: klient, vozidlo, cenotvorba, pojištění a služby, sazby za km',
        ],
      },
      {
        heading: '🛠️ Drobná vylepšení a opravy',
        items: [
          'Hodnotu faktury u vozidla lze zadat s DPH i bez — druhá hodnota se dopočítá automaticky',
          'Dialog výbavy je na desktopu širší (názvy položek a sazba DPH se už nezkracují) a na telefonu se řádky přeskládají tak, aby nic nepřetékalo',
          'Karta vozidla zobrazuje ve specifikaci i verzi a interní poznámku jde doplnit, i když byla dosud prázdná',
          'Opravena záludná chyba, kdy nabídka po přepnutí značky mohla odeslat původní značku vozu',
        ],
      },
    ],
  },
  {
    version: 'v8.1.0',
    date: '7. 7. 2026',
    sections: [
      {
        heading: '🎯 Fleet policy a retailové programy (AI)',
        items: [
          'Nová záložka Fleet policy ve správě vozidel: nahrajete čtvrtletní PDF vyhlášení programu a AI z něj vytěží kompletní tabulku podpor ke kontrole — uložením vzniká nová verze, starší zůstávají v historii',
          'Vedle fleetových pásem A/B/C jsou podporované i retailové programy zvlášť pro osobní a užitkové vozy (cenové zvýhodnění, příspěvek FMC a AP)',
          'Detail vozidla i nabídky nově zobrazuje panel s podporami, které odpovídají konkrétnímu vozu (model, modelový rok, pohon) — včetně pravidel programu a odkazu na zdrojové PDF',
          'Průběh AI vytěžování sledujete živě; při výpadku spojení zůstává PDF uloženo a vytěžení stačí spustit znovu',
        ],
      },
      {
        heading: '📥 Import ceníku Nissan (XLSX)',
        items: [
          'Ceníky ve správě vozidel umí u značky Nissan jedním kliknutím načíst velkoobchodní ceník: AI z jednotlivých listů vytěží entity, barvy, interiéry i příplatkové pakety pro všechny modely najednou (modelový rok se ukládá do provedení, např. „Acenta MY26“)',
          'Před zápisem projdete kontrolu s porovnáním proti aktuálnímu ceníku: nové položky, změny cen s vyčísleným rozdílem a entity, které v novém ceníku chybí (volitelně je lze deaktivovat)',
          'Ruční výbavy (např. sada Winter) se při nahrazení entity přenesou automaticky; každý import se eviduje včetně zdrojového sešitu ke stažení',
        ],
      },
      {
        heading: '🛠️ Správa a opravy',
        items: [
          'Firmu lze nově v Managementu kompletně smazat včetně všech dat — chráněno opsáním přesného názvu firmy (admin)',
          'Přílohy s diakritikou v názvu se opět správně stahují a otevírají',
          'Nahrávání souborů nad 1 MB (přílohy, PDF programů, ceníky) už neselhává chybou „413 Request Entity Too Large“',
        ],
      },
    ],
  },
  {
    version: 'v8.0.0',
    date: '3. 7. 2026',
    sections: [
      {
        heading: '🎉 První stabilní verze nového intranetu',
        items: [
          'Beta období končí — nové rozhraní je hotové a je nyní jediné. Staré rozhraní (PHP) bylo definitivně vyřazeno a aplikace běží přímo na hlavní adrese, znatelně rychleji',
          'Přihlášení nově vydrží i po zavření prohlížeče — nemusíte se přihlašovat každé ráno znovu',
          'Hesla jsou uložena moderním šifrovacím standardem (bcrypt) místo zastaralého otisku — výrazně vyšší ochrana účtů',
        ],
      },
      {
        heading: '📊 Výkazy EBM (Ford) v Managementu',
        items: [
          'Nový nástroj pro měsíční výkazy EBM: nahrajete export výkazu, aplikace účty automaticky přiřadí podle verzovaného mapování účtů na OZN a porovná měsíce mezi sebou',
          'Mapování účtů (OZN) lze upravovat přímo v aplikaci — nové účty se zvýrazní a doplníte je na pár kliknutí',
        ],
      },
      {
        heading: '🚗 Párování SPZ',
        items: [
          'Nová záložka v Managementu doplní chybějící SPZ u vozidel podle předávacích protokolů — hromadně a s náhledem, co se přiřadí, než se cokoli uloží',
        ],
      },
      {
        heading: '📇 Kontakty',
        items: [
          'Zprávu ke kontaktu zapíšete rovnou ze seznamu, bez otevírání detailu',
          'Přehlednější seznam — upravené sloupce a štítky podle typu kontaktu',
        ],
      },
      {
        heading: '🛠️ Drobná vylepšení',
        items: [
          'Ceny výbavy jde zadávat s DPH i bez — druhá hodnota se dopočítá automaticky (průvodce nabídkou, výbava smlouvy i formulář vozidla)',
          'Přiřazení zákazníka ke smlouvě se propíše i na navázané vozidlo',
          'Vyladěné tiskové sestavy (konfigurace vozidla, nabídkové listy, servisní intervaly) a oznámení se už nepropisují do tisku',
        ],
      },
      {
        heading: '🔧 Pod kapotou: spolehlivost',
        items: [
          'Databáze dostala kompletní integritní vazby — související záznamy se mažou spolu a nemohou vznikat osiřelá data',
          'Každá změna struktury databáze se nasazuje řízenou migrací s automatickou zálohou předem',
          'Nasazování nových verzí probíhá zcela bez výpadku',
        ],
      },
    ],
  },
  {
    version: 'v8.0.0-beta11',
    date: '17. 6. 2026',
    sections: [
      {
        heading: '✅ Nový modul Úkoly',
        items: [
          'Přibyl samostatný modul Úkoly — úkol si můžete založit sobě, nebo ho zadat kolegovi, s nepovinným termínem připomínky',
          'Všichni vidí úkoly celé firmy; přepínačem „Moje“ si zobrazíte jen ty svoje (výchozí zobrazení), filtrovat lze i podle příjemce a stavu (aktivní / splněné / zrušené)',
          'Úkol má vlastní detail i formulář pro úpravu; splnit, zrušit nebo upravit může jen jeho zadavatel nebo příjemce, smazat pouze zadavatel',
          'Na úvodní stránce v sekci „Jen pro vás“ najdete své úkoly po termínu a na nejbližší dny i úkoly, které jste zadali kolegům a jsou po termínu — a rychlé tlačítko „Nový úkol“',
        ],
      },
      {
        heading: '🔔 Upozornění na Slacku',
        items: [
          'Když někdo založí úkol, příjemce dostane upozornění přímo do Slacku jako soukromou zprávu — včetně termínu a odkazu na úkol',
          'Každé ráno v 9:00 vám přijde na Slack souhrn vašich plánovaných kontaktů a úkolů na daný den',
          'Spárování se Slackem probíhá automaticky podle e-mailu; v nabídce u svého profilu si notifikace můžete vypnout nebo se spárovat ručně',
          'Napojení na Slack nastaví administrátor v sekci Management u dané firmy (vč. tlačítka pro odeslání testovací zprávy)',
        ],
      },
      {
        heading: '📅 Úkoly v kalendáři',
        items: [
          'Kalendář nově zobrazuje i úkoly v den jejich připomínky vedle událostí a předání vozidel',
          'Přepínač „Moje“ omezí kalendář na vaše úkoly, zaškrtávátko „Úkoly“ je umí skrýt úplně — obě volby se pamatují',
        ],
      },
    ],
  },
  {
    version: 'v8.0.0-beta10',
    date: '11. 6. 2026',
    sections: [
      {
        heading: '📱 Nové rozhraní funguje na telefonu i tabletu',
        items: [
          'Celá nová aplikace se přizpůsobí displeji — na telefonu a na iPadu na výšku dostanete horní lištu s plnohodnotným menu, na širších obrazovkách zůstává postranní lišta s panelem detailu',
          'Seznamy (vozidla, kontakty, zákazníci, pošta, leasing, servis…) se na menších displejích zobrazují jako přehledné karty — klepnutím na kartu přejdete rovnou na detail',
          'Formuláře a průvodci (nabídky, smlouvy, leasing) se skládají do jednoho sloupce s plovoucím ukládacím pruhem, takže se pohodlně vyplňují i palcem',
          'Dialogy se na telefonu otevírají jako spodní panel, který zavřete gestem; oznámení se zobrazují nahoře, aby nepřekrývala tlačítka pro uložení',
        ],
      },
      {
        heading: '🔧 Servis a kalendář na cestách',
        items: [
          'Plánování servisu má na telefonu novou podobu denní agendy — pás dnů, vytížení dílny a úkony seřazené po hodinách se zvýrazněním právě probíhajícího „Nyní“',
          'Měsíční kalendář, uskladnění pneumatik, historie výměn i správa lokací fungují na mobilu v plném rozsahu',
        ],
      },
      {
        heading: '🖨️ Tisk dokumentů',
        items: [
          'Tiskové sestavy (objednávky, protokoly, přehledy) nabídnou na telefonu a tabletu tlačítko „Zobrazit PDF“ — dokument se vygeneruje ve stejné podobě jako na počítači',
        ],
      },
    ],
  },
  {
    version: 'v8.0.0-beta9',
    date: '1. 6. 2026',
    sections: [
      {
        heading: '🚗 Leasing — dokončení přechodu do nového rozhraní',
        items: [
          'Přehledy splátek a úvěrů jsou nově přímo v novém rozhraní — přepínání mezi sestavami, měsíční filtr, součty a označení ukončených smluv „Ukončeno“',
          'Všeobecné obchodní podmínky (VOP) otevřete jedním kliknutím přímo z navigace leasingu',
          'Částky se v přehledech správně dopočítávají včetně DPH podle sazby vaší firmy, stejně jako v původní aplikaci',
        ],
      },
      {
        heading: '📋 Kopírování mezi firmami',
        items: [
          'Leasingovou smlouvu lze nově zkopírovat do jiné firmy přímo z detailu — průvodce vás provede výběrem cílové firmy a zákazníka (existujícího, nebo nově vytvořeného z původního)',
          'U nabídek vozidel přibyla volba zkopírovat nabídku do jiné firmy; běžné duplikování v rámci vaší firmy zůstává beze změny (kopie do jiné firmy z bezpečnostních důvodů nepřenáší interní poznámku)',
        ],
      },
    ],
  },
  {
    version: 'v8.0.0-beta8',
    date: '27. 5. 2026',
    sections: [
      {
        heading: '🔧 Servis a plánování',
        items: [
          'Servisní události v plánovacím kalendáři nově nesou barevnou tečku autora — na první pohled je vidět, kdo událost vytvořil',
          'V náhledu dne se právě probíhající úkon zvýrazní podbarvením a označením „Nyní“, takže je hned jasné, co se zrovna dělá',
          'Náhled dne nově zobrazuje všechny naplánované úkony, ne jen prvních šest',
          'Na detailu servisní události se nově zobrazuje, kdo a kdy ji vytvořil',
        ],
      },
      {
        heading: '📇 Kontakty',
        items: [
          'U sloupce Prodejce v seznamu kontaktů přibyla barevná tečka přiřazeného prodejce pro rychlejší orientaci',
          'Kontakty s podepsanou objednávkou jsou nově v seznamu jemně zvýrazněné',
        ],
      },
    ],
  },
  {
    version: 'v8.0.0-beta7',
    date: '27. 5. 2026',
    sections: [
      {
        heading: '🚪 Nové rozhraní jako výchozí',
        items: [
          'Po přihlášení vás aplikace nově rovnou přivítá v novém rozhraní — do staré verze se kdykoliv přepnete jedním kliknutím a zpět také, stará verze zůstává plně funkční',
          'Pokud máte raději starou verzi, zůstane vaše volba zachována a budete v ní nadále přistávat',
          'Přihlašování probíhá přes novou přihlašovací obrazovku; přihlášení je i nadále sdílené mezi oběma verzemi, takže není potřeba se hlásit dvakrát',
        ],
      },
      {
        heading: '🔒 Oprávnění a přístup',
        items: [
          'Nové rozhraní nově respektuje vaše modulová oprávnění úplně stejně jako stará aplikace — moduly, ke kterým nemáte přístup, se v menu vůbec nezobrazí a nelze se na ně dostat ani přes přímý odkaz',
          'Uživatelé s přístupem pouze do servisu jsou nově korektně omezeni jen na servisní modul',
          'Sekce Management je v menu dostupná pouze administrátorům',
          'Stejná pravidla nově vynucuje i server, takže přístup k datům je chráněný i mimo rozhraní',
        ],
      },
      {
        heading: '🛞 Uskladnění pneumatik',
        items: [
          'Úprava uskladněné sady nově nabízí stejné našeptávání zákazníka a vozidla jako při zakládání nové sady',
        ],
      },
    ],
  },
  {
    version: 'v8.0.0-beta6',
    date: '26. 5. 2026',
    sections: [
      {
        heading: '🚗 Vozidla a smlouvy',
        items: [
          'Akce z karty vozidla nyní fungují přímo v novém rozhraní — Duplikovat, Duplikovat do ojetých, Předat vozidlo i Tisk konfigurace už nevyžadují přepnutí do staré aplikace',
          'Při zakládání nového předávacího protokolu se VIN, kód barvy a číslo objednávky nově automaticky předvyplní z navázaného vozidla, stejně jako v původní aplikaci',
          'Na detailu vozidla se nově zobrazuje sekce Standardní výbava',
          'U ojetých vozů funguje akce Předat zákazníkovi přímo v novém rozhraní',
        ],
      },
      {
        heading: '🔗 Propojení napříč moduly',
        items: [
          'Odkazy z detailu zákazníka na navázaná ojetá vozidla, kontakty a leasingové smlouvy nově otevírají nové rozhraní místo staré aplikace',
          'Odkaz na kanonického zákazníka v nástroji pro deduplikaci míří do nového rozhraní',
          'Otevírání příloh už nepoužívá starý mechanismus a funguje konzistentně v celé aplikaci',
        ],
      },
      {
        heading: '🛞 Uskladnění pneumatik',
        items: [
          'Nová stránka detailu uskladněné sady v novém rozhraní — úprava údajů, změna stavu i evidence sezónní výměny přímo zde',
          'Odkazy z historie výměn nově otevírají detail sady v novém rozhraní',
          'Nový nástroj v Managementu pro sloučení duplicitních lokací uskladnění, včetně ručního sloučení lokací, které detektor sám nerozpozná (admin)',
          'Sjednotili jsme akci pro otevření detailu v bočním náhledu',
        ],
      },
    ],
  },
  {
    version: 'v8.0.0-beta5',
    date: '25. 5. 2026',
    sections: [
      {
        heading: '🔧 Servis a plánování',
        items: [
          'Opravili jsme zakládání nových servisních událostí v novém rozhraní — formulář v některých případech hlásil chybu a šel uložit jen ve staré aplikaci',
          'V plánování už nelze zvolit konec dříve než začátek a čas se nově nabízí po půlhodinách, stejně jako v původní aplikaci',
          'Doplnili jsme kontrolu pracovní doby (začátek 7:00–16:00, konec 8:00–17:00) a zákaz plánování do minulosti — při chybě se zobrazí srozumitelné upozornění přímo u pole',
          'Náhled obsazenosti technika při plánování nově ukazuje i směny, takže je hned vidět, kdy má technik směnu',
        ],
      },
    ],
  },
  {
    version: 'v8.0.0-beta4',
    date: '22. 5. 2026',
    sections: [
      {
        heading: '🖨️ Tisk a sestavy',
        items: [
          'Kompletně přepracovaný vzhled tištěné nabídky i smlouvy — nová hlavička s názvem a adresou firmy (u smlouvy včetně zápisu v rejstříku a bankovního spojení), modře zvýrazněné nadpisy sekcí a jemnější linky pro snadné prolétnutí dokumentu',
          'Odstranili jsme zdvojený blok dodavatele — firmu už identifikuje hlavička, takže blok zákazníka je nově přes celou šířku a dokument je kompaktnější',
          'Výraznější závěrečný panel s cenou (bez i včetně DPH ve stejné váze), zebrované tabulky výbavy a zvýrazněný blok vozidla',
          'U smlouvy se v záhlaví zobrazuje datum uzavření místo zdvojeného data předání',
          'Při ukládání do PDF se nově nabídne smysluplný název souboru — např. „Cenová nabídka <číslo> – <zákazník>“ / „Závazná objednávka …“',
        ],
      },
      {
        heading: '💰 Slevy',
        items: [
          'Vrátili jsme do průvodce nabídkou i smlouvou pole Sleva včetně DPH — zadání bez DPH a včetně DPH se nyní přepočítává v reálném čase podle sazby DPH nabídky, stejně jako v původní aplikaci',
          'Oprava: procentuální sleva se při úpravě nabídky/smlouvy nově předvyplní správně do pole „%“ (dříve se např. 27 % chybně objevilo jako „27 Kč“)',
        ],
      },
      {
        heading: '📅 Kalendář a plánování',
        items: [
          'V týdenním plánování servisu lze nově naplánovat událost přímo kliknutím na volný časový slot; minulé sloty jsou nedostupné a sloty mají popisky pro lepší přístupnost',
          'Při plánování se zobrazuje náhled možných překryvů, aby bylo hned vidět, zda se termín kryje s jiným úkonem',
        ],
      },
    ],
  },
  {
    version: 'v8.0.0-beta3',
    date: '22. 5. 2026',
    sections: [
      {
        heading: '👤 Kontakty',
        items: [
          'Vrátili jsme správu životního cyklu kontaktu do nového rozhraní — kontakt lze přímo z detailu i z úprav uzavřít, znovu otevřít a předat jinému prodejci',
          'Při uzavření kontaktu z LMS se nově důvod nezájmu (nebo podpis objednávky) propíše zpět do LMS — dříve se vybraný důvod nikam neukládal',
          'Export kontaktů — seznam lze nově stáhnout do CSV přímo z panelu nástrojů; export respektuje aktuální filtry (typ, značka, prodejce) a zvolené období posledního kontaktu',
          'Plánované kontakty — v bočním náhledu se nově zobrazují základní údaje o kontaktu (telefon, e-mail) i poznámka, takže lze plán vyřídit bez otevírání celého detailu',
        ],
      },
      {
        heading: '✨ Vzhled a ovládání formulářů',
        items: [
          'Sjednotili jsme záhlaví sekcí ve všech formulářích a průvodcích — odstranili jsme zdvojené nadpisy, zmenšili nadbytečné mezery a zarovnali název sekce k pořadovému číslu pro klidnější a přehlednější vzhled',
          'Pole Poznámka se nově automaticky zvětšuje podle množství textu — žádné posouvání v malém okénku',
        ],
      },
    ],
  },
  {
    version: 'v8.0.0-beta2',
    date: '21. 5. 2026',
    sections: [
      {
        heading: '🖨️ Tisk a sestavy',
        items: [
          'Zápatí tiskových sestav (nabídky, smlouvy, předávací protokoly) se nyní správně opakuje na každé stránce a už nepřekrývá obsah na dalších stranách',
          'Sjednocené číslo dokumentu vpravo nahoře — odstranili jsme symbol „№“, který se v češtině pletl s „No“',
          'Sleva v procentech se v souhrnu i na tisku zobrazuje čistě (např. „19,5 %“ místo „19,5000 %“)',
        ],
      },
      {
        heading: '💰 Ceny a DPH',
        items: [
          'Rozpad podle sazeb DPH se nově propsal i do souhrnu v detailu smlouvy (dříve jen u nabídek)',
          'Sleva se počítá pouze ze základní ceny a originální výbavy — dodatečná výbava je z výpočtu slevy správně vyloučena',
          'Vyčištěné a srozumitelnější zobrazení faktur a souhrnů u nabídek i smluv',
        ],
      },
      {
        heading: '✨ Nové funkce a filtry',
        items: [
          'Vozidla — do seznamu přibyl filtr podle modelu a podle měsíce předání',
          'Nabídky — v detailu se zobrazují Záruky a servisní smlouvy',
          'Průvodce nabídkou i novým vozidlem nově nabízí výběr výbavy podle značky — Ford přes katalog LMS (model → modelový rok → ceník → konfigurace), ostatní značky přes lokální ceníky (model → entita → interiér → barva); vrací zpět značkově specifické postupy z původní aplikace',
          'Číslo zakázky — nově lze filtrovat a vyhledávat podle čísla zakázky napříč vozidly, nabídkami i smlouvami, včetně rychlého hledání v Command Palette (⌘K / Ctrl+K)',
          'LMS katalog — tabulku konfigurací lze nově řadit kliknutím na záhlaví sloupců; lepší mapování a zobrazení modelů a doplněné konfigurační atributy vozidla',
          'Při načítání dat z LMS se v rozbalovacích nabídkách zobrazují jasné stavy načítání a chyb — žádné záhadně prázdné seznamy při pomalém spojení',
          'Levý panel se po najetí myší rozbalí na plné popisky',
        ],
      },
      {
        heading: '👤 Kontakty a zákazníci',
        items: [
          'Nový kontakt se automaticky propojí se zákazníkem podle shody IČO; propojení lze provést i ručně z detailu kontaktu',
          'Formulář kontaktu nově vyžaduje jméno/firmu, kontaktní kanál a značku + model',
          'Tlačítko „Přidat zprávu“ jsme přesunuli přímo k sekci Zprávy',
        ],
      },
      {
        heading: '🏠 Přehled (úvodní stránka)',
        items: [
          'Nové karty „Jen pro vás“ — vaše plány po termínu a na nejbližší dny, barevně zvýrazněné, když něco čeká na vyřízení',
          'Plány zobrazují název firmy, pokud chybí jméno klienta',
        ],
      },
      {
        heading: '🛠️ Stabilita a opravy',
        items: [
          'Opravili jsme servírování zastaralé stránky odkazující na již smazané soubory — méně problémů hned po nasazení nové verze',
          'Průvodce nabídkou/smlouvou — krokový pruh zůstává proklikávatelný a součet v Souhrnu je vždy aktuální; úpravy z detailu se vrací zpět na detail',
          'Oprava odkazu na zákazníka, aby nemířil na záznam z jiné firmy',
          'Ikony v hlavním menu jsou nově výraznější — silnější tah a sytější barva pro lepší čitelnost',
        ],
      },
    ],
  },
  {
    version: 'v8.0.0-beta1',
    date: '18. 5. 2026',
    sections: [
      {
        heading: '🚀 Nový Intranet — kompletně přepracované rozhraní',
        items: [
          'Spouštíme zbrusu novou verzi celé aplikace postavenou na moderním stacku (React + TypeScript), s rychlejším načítáním, plynulou navigací bez znovunačítání stránek a sjednoceným designem napříč všemi moduly',
          'Přepínání mezi starou a novou verzí je kdykoliv k dispozici — v menu i přes pruh nahoře. Stará verze zůstává plně funkční, novou si můžete vyzkoušet bez rizika a kdykoliv se vrátit zpět',
          'Přihlášení je sdílené — z legacy aplikace se do nové dostanete jedním kliknutím bez nutnosti opětovného přihlášení',
        ],
      },
      {
        heading: 'Co všechno už v nové verzi najdete',
        items: [
          'Vozidla (nová i ojetá) — výpisy, detaily, kompletní průvodce pro nabídky a smlouvy (zákazník → vozidlo → výbava → sleva → financování → souhrn), předávací protokoly, tiskové sestavy A4, sekce Upravená vozidla, LMS synchronizace, výkup vozidel',
          'Leasing — vyhledávání, detail smlouvy, 4-krokový průvodce, předávací protokol, formulář slev / financování / nákladů',
          'Zákazníci a Kontakty — souhrnné detaily s vazbami na vozidla, smlouvy, kontakty a uskladněné pneu, bohatší výběr zákazníka napříč všemi průvodci, plánované kontakty',
          'Servis — swimlane plánování po mechanikách, vyhledávání servisních událostí, uskladnění pneumatik, historie výměn, správa lokací, real-time aktualizace přes PubNub',
          'Kalendář — měsíční a týdenní pohled, vytváření, editace a mazání událostí přímo v modálu, deep-link z karet smluv na termín předání',
          'Pošta — výpis zásilek, detail, formulář pro novou i editaci zásilky',
          'Management — administrativní centrum s pěti záložkami: Ceníky, Deduplikace zákazníků, Uživatelé, Firmy, LMS katalog',
          'Dashboard a přihlášení — nová úvodní stránka s přehledem a samostatná přihlašovací obrazovka na /login',
        ],
      },
      {
        heading: 'Novinky v ovládání a vzhledu',
        items: [
          'Tmavý, světlý a systémový režim — volba se ukládá k vašemu uživatelskému profilu a synchronizuje se mezi zařízeními',
          'Command Palette (⌘K / Ctrl+K) — rychlý skok na jakoukoliv stránku v aplikaci přes vyhledávání',
          'Stránkování s nekonečným scrollem na všech přehledech místo klikání po stránkách',
          'Plynulé přechody mezi stránkami (View Transitions), animovaná otevírání modálů, inspektorů, save baru a notifikací',
          'Vylepšená přístupnost (WCAG) — automaticky kontrolováno v CI nástrojem axe-core, plná podpora klávesnice a screen readerů',
        ],
      },
      {
        heading: 'Pod kapotou',
        items: [
          'Všechna data tečou přes nové REST API (/v1/*) s přísnou validací, ověřením členství ve firmě a typovými kontrakty sdílenými mezi backendem a frontendem',
          'Nové admin endpointy pro správu uživatelů, firem a členství; přepracovaný auth flow s bearer tokeny',
          'Lazy loading modálů a stránek — menší přenesená data a rychlejší první vykreslení',
        ],
      },
    ],
  },
  {
    version: 'v7.8.0',
    date: '29. 4. 2026',
    sections: [
      {
        heading: 'Zákazníci – Deduplikace',
        items: [
          'Nový nástroj v Managementu pro detekci a sloučení duplicitních zákazníků v rámci firmy (admin)',
          'Detekce po 3 úrovních: shoda IČO, e-mailu, nebo jména s potvrzujícím polem (telefon / zástupce / adresa)',
          'Sloučení přesměruje všechna navázaná data (vozidla, smlouvy, nabídky, kontakty, servisní události, uskladněné pneu) na zvolený kanonický záznam, duplikáty jsou smazány v jedné transakci',
          'Řešení konfliktů hodnot per pole, nahrazení dat z ARES (u IČO skupin)',
          'Audit log všech sloučení (kanonický + smazaný + JSON snapshot, přehled v UI)',
        ],
      },
      {
        heading: 'Zákazníci – Prevence duplikátů při zakládání',
        items: [
          'Při ukládání nového zákazníka se kontroluje, zda už neexistuje (ve stejné firmě)',
          'Tvrdé blokování při shodě IČO — možnost rovnou „Použít existujícího" a navázat ho na rozjetou smlouvu / nabídku / vozidlo',
          'Měkké varování při shodě e-mailu, telefonu nebo podobného jména + adresy / zástupce — uživatel může „Přesto vytvořit nového"',
        ],
      },
    ],
  },
  {
    version: 'v7.7.0',
    date: '28. 4. 2026',
    sections: [
      {
        heading: 'Servis – Uskladnění pneumatik',
        items: [
          'Nový submodul pro správu uskladněných pneumatik (klientské sady, sklad, volné kusy)',
          'Evidence sezónních výměn — propojení sundané a nasazené sady, datum, mechanik, poznámka',
          'Kompletní historie změn každé sady (audit log) a historie výměn na vůz / klienta',
          'Autocomplete pro umístění (lokace v garáži / kontejneru), správa lokací s počtem uskladněných sad',
          'Vyhledávač zákazníků a vozidel přímo na formuláři pro novou sadu / výměnu — sada se propojí se záznamem v databázi (typeahead přes SPZ, číslo objednávky nebo VIN)',
          'Sekce „Uskladněné pneumatiky" na detailu vozidla (nového i ojetého) a zákazníka — okamžitý přehled co kde mají uloženo + tlačítko pro založení nové sady s předvyplněným propojením',
          'Sloupec „Vozidlo" (značka + model) na seznamu sad, aktuální jméno klienta čteno přes propojení (ne ze snapshotu)',
          'Hromadný import legacy xlsx evidence z managementu (admin)',
          'Real-time aktualizace přes PubNub při souběžné editaci',
        ],
      },
    ],
  },
  {
    version: 'v7.5.3',
    date: '9. 4. 2026',
    sections: [
      {
        heading: 'Servis',
        items: [
          'Kompletní modernizace kalendáře servisu pomocí CSS Grid — spolehlivé vykreslování událostí, směn a vícedenních událostí',
          'Opraveno zobrazení při kombinaci směn a vícedenních událostí u jednoho mechanika',
          'Náhled vícedenních událostí je nyní podporován v plánovacím formuláři',
        ],
      },
    ],
  },
  {
    version: 'v7.4.0',
    date: '20. 3. 2026',
    sections: [
      {
        heading: 'Infrastruktura',
        items: [
          'Migrace modulů pošta, ojetá vozidla, leasing, vozidla, zákazníci a servis na nové REST API',
          'Vylepšené zabezpečení přístupu s ověřením členství uživatele ve firmě a přísnější validací vstupních dat',
          'Blue/green nasazování pro bezpečnější aktualizace bez výpadků',
        ],
      },
      {
        heading: 'Servis',
        items: [
          'Modul servis kompletně migrován na nové API — rychlejší načítání a lepší stabilita',
          'Správné zobrazení časů událostí v české časové zóně',
        ],
      },
      {
        heading: 'Zákazníci',
        items: [
          'Nový souhrnný přehled zákazníka s vazbami na vozidla, leasing a kontakty',
          'Vylepšený výběr firmy s přehlednějším rozhraním',
        ],
      },
      {
        heading: 'Vozidla',
        items: [
          'Podpora příznaku „bez vozidla" při uzavírání smluv s automatickým doplněním vozu ze smlouvy',
          'Kopírování doplňků a přílohy při duplikaci nabídek',
        ],
      },
      {
        heading: 'Ojetá vozidla',
        items: [
          'Zobrazení fotografií z S3 úložiště přímo v kartě ojetého vozu',
          'Kompletní funkce výkupu vozidel s vyhledáváním podle VIN',
        ],
      },
    ],
  },
  {
    version: 'v7.3.0',
    date: '11. 3. 2026',
    sections: [
      {
        heading: 'Vozidla',
        items: [
          'Z karty nového vozu lze nově jedním klikem vytvořit ojetý vůz; zároveň byl doplněn Modelový rok a Číslo skladové karty přímo na kartu vozu',
          'Ford vozy založené z LMS si nyní automaticky přenášejí modelový rok (např. 2026.75)',
          'Ve výpisu vozidel byl zjednodušen horní filtr, v přehledu upravených vozidel je nově sjednocen popisek Prodejní cena a stav Předaný dealerovi pracuje s dealerem jako s volným textem',
        ],
      },
      {
        heading: 'Ojetá vozidla',
        items: [
          'Přidán přehled Upravená vozidla i pro ojeté vozy, včetně evidence změn prodejní ceny',
          'Seznam ojetých vozů lze otevřít bez předvybrané značky a zobrazit všechny značky/modely najednou',
          'Opraveno založení nového ojetého vozu, aby se VIN zachovalo už při prvním uložení',
          'Při vytvoření nabídky z ojetého vozu se nově správně přenáší stav km a první registrace, zároveň byla opravena editace modelu vozu v nabídce',
        ],
      },
      {
        heading: 'Servis',
        items: [
          'Vyhledávání servisních událostí nově vrací pouze klasické servisní úkony, bez interních úkonů, směn a dovolených',
          'Přidán jednorázový e-mailový reminder 48 hodin před servisním úkonem',
        ],
      },
      {
        heading: 'Kontakty',
        items: [
          'Sjednoceno pořadí voleb v rozbalovacích filtrech, aby byla volba Všechny vždy nahoře',
          'Při duplikaci kontaktu se nově první návštěva i poslední kontakt nastaví na datum duplikace',
        ],
      },
    ],
  },
  {
    version: 'v7.2.1',
    date: '17. 2. 2026',
    sections: [
      {
        heading: 'Kontakty',
        items: [
          'Přidána akce Duplikovat kontakt (bez historie zpráv/plánů/příloh), nový kontakt je otevřený a přiřazen aktuálnímu uživateli, zachovává vazbu na zákazníka a resetuje LMS ID; zároveň zpřísněna tenant kontrola detailu a lokalizovány validační chyby do češtiny',
        ],
      },
    ],
  },
  {
    version: 'v7.2.0',
    date: '10. 12. 2025',
    sections: [
      {
        heading: 'Nahrávání souborů',
        items: [
          'Přidána podpora pro Microsoft Office soubory (Word, Excel, PowerPoint) při nahrávání příloh',
          'Zvýšen maximální limit velikosti souboru z 10MB na 20MB',
        ],
      },
    ],
  },
  {
    version: 'v7.1.0',
    date: '4. 12. 2025',
    sections: [
      {
        heading: 'Evidence nabíjení vozidel',
        items: [
          'Nová funkcionalita pro sledování stavu baterie vozidel s historií nabíjení',
          'Barevný indikátor (zelená/oranžová/červená) podle dnů od nabití a úrovně baterie',
        ],
      },
    ],
  },
  {
    version: 'v7.0.0',
    date: '26. 11. 2025',
    sections: [
      {
        heading: 'Infrastruktura - Největší aktualizace v historii Intranetu',
        items: [
          'Kompletní migrace z platformy Heroku na Amazon Web Services (AWS)',
          'Nasazení aplikace pomocí Docker kontejnerů pro lepší stabilitu a škálovatelnost',
          'Nová infrastruktura na AWS EC2 s automatickým nasazováním přes GitHub Actions',
          'Využití AWS ECR pro správu Docker obrazů',
          'Implementace Traefik reverse proxy s automatickými SSL certifikáty',
          'Vylepšená bezpečnost a výkon díky moderní kontejnerizované architektuře',
        ],
      },
    ],
  },
];

interface NovinkyPanelProps {
  /** Limit to the first N entries. Defaults to all. */
  limit?: number;
}

export function NovinkyPanel({ limit }: NovinkyPanelProps) {
  const entries = limit != null ? NOVINKY.slice(0, limit) : NOVINKY;
  return (
    <section
      data-testid="novinky-panel"
      className="border border-line bg-paper px-6 py-5"
    >
      <header className="mb-4 flex items-baseline justify-between border-b border-line pb-3">
        <h2 className="font-display text-[28px] leading-none tracking-[-0.02em]">
          Novinky, <em className="font-light italic text-ink-3">v aplikaci.</em>
        </h2>
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          {entries.length} · z {NOVINKY.length}
        </span>
      </header>

      <div className="space-y-6">
        {entries.map((entry) => (
          <article key={entry.version} className="border-l border-line pl-4">
            <div className="mb-2 flex items-baseline justify-between gap-4">
              <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-accent-text">
                {entry.version}
              </h3>
              <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                {entry.date}
              </span>
            </div>
            <div className="space-y-3">
              {entry.sections.map((section) => (
                <div key={section.heading}>
                  <h4 className="mb-1 text-[13px] font-semibold text-ink-2">
                    {section.heading}
                  </h4>
                  <ul className="space-y-1 text-[13px] leading-snug text-ink">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex gap-2">
                        <span aria-hidden className="mt-2 size-1 shrink-0 bg-ink-3" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
